import React, {useState, useMemo} from 'react';
import {
  Box,
  Flex,
  Input,
  Button,
  Spinner,
  FormControl,
  FormLabel,
  FormHelperText,
} from '@chakra-ui/react';
import {WizardContent} from '../../Agents/AgentBuilder/wizard/WizardContent';
import {ObjectId} from 'bson';
import {useGetTestLists} from '../../../hooks/api/testList';
import {useTask} from '../../../hooks/api/tasks';
import {MinSegmentationTestListSize} from '../../../shared/agent';
import {Controller, useForm} from 'react-hook-form';
import {useGenerateSegments} from '../../../hooks/api/segments';
import Select from 'react-select';
import {SegmentationChatInterface} from './SegmentationChatInterface';

export const SegmentationChat = () => {
  const testLists = useGetTestLists({});
  const generateSegments = useGenerateSegments();

  const [taskId, setTaskId] = useState<ObjectId | null>(null);
  const {isTaskInProgress} = useTask(
    {type: 'generateSegments'},
    {
      onTaskComplete: (task) => {
        setTaskId(task.id);
      },
    }
  );

  const testListOptions = useMemo(
    () =>
      (testLists.data ?? []).map((testList) => ({
        label: testList.label,
        value: testList._id,
        isDisabled:
          testList.status.type === 'processing' ||
          testList.status.numAccounts < MinSegmentationTestListSize,
      })),
    [testLists.data]
  );

  const {
    control,
    register,
    handleSubmit,
    formState: {errors},
  } = useForm<{
    domain: string;
    testList: {
      label: string;
      value: ObjectId;
    };
  }>();

  const onGenerateSegments = handleSubmit((data) => {
    const {domain, testList} = data;
    if (!testList || !domain) {
      return;
    }

    generateSegments.mutate({
      domain,
      testListId: testList.value,
    });
  });

  if (isTaskInProgress || generateSegments.isPending) {
    return (
      <WizardContent
        title="Your segments are being generated..."
        footer="This usually takes ~20-35 seconds"
        bgColor="kgray.50"
      >
        <Spinner size="xl" color="kred.300" />
      </WizardContent>
    );
  }

  if (taskId) {
    return <SegmentationChatInterface taskId={taskId} />;
  }

  return (
    <WizardContent title="Segmentation Chat" bgColor="kgray.50">
      <Box as="form" onSubmit={onGenerateSegments} maxW="600px" w="100%">
        <Flex direction="column" gap="10">
          <FormControl isInvalid={!!errors.testList}>
            <FormLabel>
              Select a customer list to discover your high-value segments
            </FormLabel>
            <Controller
              name={'testList'}
              control={control}
              rules={{required: true}}
              render={({field, fieldState: {error}}) => (
                <Select
                  {...field}
                  isLoading={testLists.isPending}
                  options={testListOptions}
                  styles={{
                    control: (base) => ({
                      ...base,
                      // Outline the component in red when there is an error
                      borderColor: error ? 'red' : base.borderColor,
                      boxShadow: error ? '0 0 0 1px red' : base.boxShadow,
                      '&:hover': {
                        borderColor: error ? 'red' : base.borderColor,
                      },
                    }),
                  }}
                />
              )}
            />
            <FormHelperText>
              Minimum test list size is {MinSegmentationTestListSize}.
            </FormHelperText>
          </FormControl>

          <FormControl isInvalid={!!errors.domain}>
            <FormLabel>Enter your website URL for context</FormLabel>
            <Input
              autoComplete="off"
              bgColor="white"
              placeholder="e.g. example.com"
              {...register('domain', {
                required: true,
              })}
            />
          </FormControl>

          <Button colorScheme="kbuttonblue" type="submit" w="fit-content">
            Generate Segments
          </Button>
        </Flex>
      </Box>
    </WizardContent>
  );
};
